/**
 * Favoriten Helper für Subcollections
 * Speichert nur: strasse, plz, ort (KEINE Hausnummer, KEIN Empfänger)
 */

import { 
  getFavoriten, 
  createFavorit, 
  deleteFavorit as serverDeleteFavorit, 
  updateFavorit as serverUpdateFavorit 
} from '../services/serverDataService';

/**
 * Speichert einen Favoriten in Subcollection
 * Hausnummer wird automatisch entfernt!
 */
export const saveFavorit = async (favorit, userId) => {
  if (!userId) {
    throw new Error('User ID erforderlich');
  }

  const cleanedStrasse = removeHausnummerFromStrasse(favorit.strasse || favorit.streetAndNumber || '');
  
  const favoritData = {
    strasse: cleanedStrasse,
    plz: favorit.plz || favorit.postalAndCity?.split(' ')[0] || '',
    ort: favorit.ort || favorit.postalAndCity?.split(' ').slice(1).join(' ') || '',
    name: cleanedStrasse // Server erwartet 'name' Feld
  };

  console.log('💾 Speichere Favorit (ohne Hausnummer):', favoritData);
  const result = await createFavorit(favoritData);
  return { ...favoritData, id: result?.id || crypto.randomUUID() };
};

/**
 * Lädt alle Favoriten aus Subcollection
 */
export const loadFavoriten = async (userId) => {
  if (!userId) {
    console.warn('Keine User ID - kann Favoriten nicht laden');
    return [];
  }

  console.log('📥 Lade Favoriten vom Server...');
  const favoriten = await getFavoriten();
  console.log(`📦 ${favoriten.length} Favoriten geladen`);
  return favoriten;
};

/**
 * Löscht einen Favoriten
 */
export const deleteFavorit = async (favoritId, userId) => {
  if (!favoritId) {
    throw new Error('Favorit ID erforderlich');
  }

  await serverDeleteFavorit(favoritId);
  console.log(`🗑️ Favorit ${favoritId} gelöscht`);
};

/**
 * Aktualisiert einen Favoriten
 */
export const updateFavorit = async (favoritId, updates, userId) => {
  if (!favoritId) {
    throw new Error('Favorit ID erforderlich');
  }

  // Hausnummer aus Straße entfernen
  if (updates.strasse) {
    updates.strasse = removeHausnummerFromStrasse(updates.strasse);
  }

  await serverUpdateFavorit(favoritId, updates);
  console.log(`✏️ Favorit ${favoritId} aktualisiert`);
};

/**
 * Entfernt Hausnummer aus Straße
 * Beispiele:
 * - "Fürstenrieder Str. 279a" → "Fürstenrieder Str."
 * - "Hauptstraße 12" → "Hauptstraße"
 * - "Am Markt 5b" → "Am Markt"
 */
function removeHausnummerFromStrasse(strasse) {
  if (!strasse) return '';
  
  // Entferne alles nach letzter Zahl (inkl. Buchstaben wie "a", "b")
  // Regex: Entfernt Leerzeichen + Zahl + optionale Buchstaben am Ende
  return strasse
    .replace(/\s+\d+[a-zA-Z]?\s*$/, '') // z.B. " 279a"
    .trim();
}

/**
 * Findet ähnliche Straße in Favoriten (Fuzzy Matching)
 * Verwendet Levenshtein Distance für Ähnlichkeitsvergleich
 */
export const findSimilarStrasse = (gescannteStrasse, favoriten, threshold = 0.7) => {
  if (!gescannteStrasse || !favoriten || favoriten.length === 0) {
    return null;
  }

  const cleanedInput = removeHausnummerFromStrasse(gescannteStrasse.toLowerCase());
  
  let bestMatch = null;
  let bestSimilarity = 0;

  for (const favorit of favoriten) {
    if (!favorit.strasse) continue;
    
    const favStrasseClean = favorit.strasse.toLowerCase();
    const similarity = calculateSimilarity(cleanedInput, favStrasseClean);
    
    if (similarity > bestSimilarity && similarity >= threshold) {
      bestSimilarity = similarity;
      bestMatch = favorit;
    }
  }

  if (bestMatch) {
    console.log(`🎯 Straßen-Match gefunden: "${gescannteStrasse}" → "${bestMatch.strasse}" (${Math.round(bestSimilarity * 100)}% Ähnlichkeit)`);
  }

  return bestMatch;
};

/**
 * Berechnet Ähnlichkeit zwischen zwei Strings (0-1)
 * Verwendet Levenshtein Distance
 */
function calculateSimilarity(str1, str2) {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  
  if (longer.length === 0) return 1.0;
  
  const distance = levenshteinDistance(longer, shorter);
  return (longer.length - distance) / longer.length;
}

/**
 * Levenshtein Distance Algorithmus
 */
function levenshteinDistance(str1, str2) {
  const matrix = [];

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // Substitution
          matrix[i][j - 1] + 1,     // Insertion
          matrix[i - 1][j] + 1      // Deletion
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
}
